/**
 * Chat-history panel — overlays the transcript inside the drawer (toggled from
 * the header) and lists, for the current scope: the user's past Velo
 * conversations (pick one to reopen + continue it) and their support requests
 * (bug reports / human escalations — pick one to jump back to the chat it came
 * from). Read-only summaries fetched lazily; refetched on scope change.
 */
import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { IconArrowLeft, IconBug, IconLifebuoy, IconMessage, IconTrash } from "@tabler/icons-react";
import { useAiAssistant } from "@/providers/AiAssistantProvider";
import type {
  AiConversationSummary,
  SupportRequestSummary,
} from "@/lib/api/clients/AiClient";

/** Compact relative time ("3h", "2d") falling back to a locale date past a week. */
function formatWhen(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "narrow" });
  if (min < 1) return rtf.format(0, "minute");
  if (min < 60) return rtf.format(-min, "minute");
  const hours = Math.round(min / 60);
  if (hours < 24) return rtf.format(-hours, "hour");
  const days = Math.round(hours / 24);
  if (days < 7) return rtf.format(-days, "day");
  return new Date(then).toLocaleDateString(locale, { day: "numeric", month: "short" });
}

export function ConversationHistoryPanel({ onClose }: { onClose: () => void }) {
  const t = useTranslations("aiAssistant");
  const locale = useLocale();
  const {
    listConversations,
    listSupportRequests,
    openConversation,
    deleteConversation,
    scopeKind,
    scopeEventId,
  } = useAiAssistant();

  const [items, setItems] = useState<AiConversationSummary[] | null>(null);
  // null = still loading; [] = loaded-empty. Tracked so the empty state waits
  // for BOTH lists (no "no chats" flash before support rows arrive).
  const [supports, setSupports] = useState<SupportRequestSummary[] | null>(null);
  const [error, setError] = useState(false);
  // Disables the lists while a pick is loading (avoid a double-open race).
  const [opening, setOpening] = useState(false);
  // Two-step delete: first trash click arms the row (button turns red), the
  // second click deletes. Leaving the row disarms.
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  // Bumped on every scope-driven refetch: a failed delete that resolves after
  // the panel switched scopes must not write into the new scope's list.
  const fetchGenRef = useRef(0);

  // Refetch when the scope changes too (the provider stays mounted across an
  // event→event / →company switch, so the open panel must not show a stale
  // event's history). Both list fns are referentially stable.
  useEffect(() => {
    let active = true;
    fetchGenRef.current += 1;
    setArmedDeleteId(null);
    setError(false);
    setItems(null);
    setSupports(null);
    // Independent best-effort fetches — a failed support list must not blank the
    // conversations and vice-versa.
    listConversations()
      .then((list) => {
        if (active) setItems(list);
      })
      .catch(() => {
        if (active) {
          setItems([]);
          setError(true);
        }
      });
    listSupportRequests()
      .then((list) => {
        if (active) setSupports(list);
      })
      .catch(() => {
        // Support list is secondary — on failure just show no support section.
        if (active) setSupports([]);
      });
    return () => {
      active = false;
    };
  }, [listConversations, listSupportRequests, scopeKind, scopeEventId]);

  // Opens the conversation a row points at (a chat, or the chat a support
  // request was filed from). No-op for a support request with no linked chat.
  const pick = async (conversationId: string | null) => {
    if (!conversationId || opening) return;
    setOpening(true);
    const ok = await openConversation(conversationId);
    if (ok) onClose();
    else {
      // Couldn't load (deleted / network) — surface the error, re-enable the list.
      setError(true);
      setOpening(false);
    }
  };

  // Optimistic delete: the row disappears immediately; on failure just THAT
  // row is re-inserted (never a full-list snapshot — a concurrent delete's row
  // must stay gone) and the shared error banner shows. Server delete is
  // idempotent, so a double-fire is harmless.
  const remove = async (conversationId: string) => {
    if (opening) return;
    setArmedDeleteId(null);
    const gen = fetchGenRef.current;
    const index = (items ?? []).findIndex((c) => c.conversationId === conversationId);
    if (index < 0) return;
    const row = (items ?? [])[index];
    setItems((list) => (list ?? []).filter((c) => c.conversationId !== conversationId));
    const ok = await deleteConversation(conversationId);
    // A scope switch mid-delete refetched the list — the restore below would
    // splice a foreign scope's row into it.
    if (ok || fetchGenRef.current !== gen) return;
    setItems((list) => {
      const current = list ?? [];
      if (current.some((c) => c.conversationId === conversationId)) return list;
      const restored = [...current];
      restored.splice(Math.min(index, restored.length), 0, row);
      return restored;
    });
    setError(true);
  };

  const bothEmpty =
    items !== null && supports !== null && items.length === 0 && supports.length === 0;

  return (
    <div className="min-h-0 flex-1 flex flex-col">
      <div className="shrink-0 flex items-center gap-2 px-3.5 h-11 border-b outline-quaternary">
        <button
          type="button"
          aria-label={t("close")}
          onClick={onClose}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-tertiary hover:surface-secondary hover:font-secondary transition-colors cursor-pointer"
        >
          <IconArrowLeft size={18} />
        </button>
        <span className="font-medium text-sm">{t("history")}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {/* Error banner shows regardless of list contents (a failed pick on a
            non-empty list must still surface). */}
        {error && (
          <p role="alert" className="px-2 py-3 text-sm font-error">
            {t("historyError")}
          </p>
        )}

        {items === null || (items.length === 0 && supports === null) ? (
          // Keep showing "loading" while conversations are empty but the support
          // list is still pending, so the empty state never flashes before it.
          <p className="px-2 py-3 text-sm font-tertiary">{t("historyLoading")}</p>
        ) : bothEmpty ? (
          !error && <p className="px-2 py-3 text-sm font-tertiary">{t("historyEmpty")}</p>
        ) : (
          <>
            {items.length > 0 && (
              <ul className="flex flex-col gap-0.5">
                {items.map((c) => {
                  const armed = armedDeleteId === c.conversationId;
                  return (
                    // The row is a group of two sibling buttons (open + delete —
                    // a nested button would be invalid HTML). The trash swaps in
                    // over the time label on hover/focus.
                    <li
                      key={c.conversationId}
                      className="group relative"
                      onMouseLeave={() =>
                        setArmedDeleteId((id) => (id === c.conversationId ? null : id))
                      }
                    >
                      <button
                        type="button"
                        disabled={opening}
                        onClick={() => void pick(c.conversationId)}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:surface-secondary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default"
                      >
                        <IconMessage size={16} className="shrink-0 font-tertiary" />
                        <span className="flex-1 min-w-0 truncate text-sm">
                          {c.title || t("untitledChat")}
                        </span>
                        <span className="shrink-0 text-[11px] font-tertiary tabular-nums transition-opacity group-hover:opacity-0 group-focus-within:opacity-0">
                          {formatWhen(c.updatedAt, locale)}
                        </span>
                      </button>
                      <button
                        type="button"
                        disabled={opening}
                        aria-label={armed ? t("deleteChatConfirm") : t("deleteChat")}
                        title={armed ? t("deleteChatConfirm") : t("deleteChat")}
                        onClick={() =>
                          armed ? void remove(c.conversationId) : setArmedDeleteId(c.conversationId)
                        }
                        className={`absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md transition-opacity cursor-pointer ${
                          armed
                            ? "opacity-100 font-error"
                            : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 font-tertiary hover:font-error"
                        }`}
                      >
                        <IconTrash size={15} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {supports && supports.length > 0 && (
              <>
                <p className="px-2.5 pt-3 pb-1 text-[11px] font-tertiary uppercase tracking-wide">
                  {t("supportRequests")}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {supports.map((s) => {
                    const Icon = s.type === "bug" ? IconBug : IconLifebuoy;
                    const label =
                      s.type === "bug" ? t("supportTypeBug") : t("supportTypeEscalation");
                    const clickable = !!s.conversationId;
                    const rowClass =
                      "group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left";
                    const inner = (
                      <>
                        <Icon size={16} className="shrink-0 font-tertiary" />
                        <span className="flex-1 min-w-0 truncate text-sm">
                          {s.message?.trim() || label}
                        </span>
                        <span className="shrink-0 text-[11px] font-tertiary tabular-nums">
                          {formatWhen(s.createdAt, locale)}
                        </span>
                      </>
                    );
                    return (
                      <li key={s.id}>
                        {clickable ? (
                          <button
                            type="button"
                            disabled={opening}
                            title={label}
                            onClick={() => void pick(s.conversationId)}
                            className={`${rowClass} hover:surface-secondary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default`}
                          >
                            {inner}
                          </button>
                        ) : (
                          <div className={rowClass} title={label}>
                            {inner}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
